import { Offer, Settings } from '../../../domain';
import { GeneralTranslations, OfferTranslations } from '../types';
import { getTranslationsForOfferById } from './getTranslationsForOfferById';
import { logError } from '../../../utils/logging';

/**
 * Batch fetch translations for multiple offers and return a map of offer ID to translations
 */
export const batchFetchOfferTranslations = async (
  storefrontAccessToken: string,
  offers: Offer[],
  generalMetaobject: Record<string, string> | null,
  settings: Settings,
): Promise<Map<number, OfferTranslations>> => {
  const translationsMap = new Map<number, OfferTranslations>();

  // Create promises for all translation fetches
  const fetchPromises = offers.map(async (offer) => {
    try {
      const translations = await getTranslationsForOfferById(
        storefrontAccessToken,
        offer,
        generalMetaobject,
        settings,
        offer.custom_wording_metaobject_id,
      );
      return { offerId: offer.id, translations };
    } catch (error) {
      logError(`Failed to fetch translations for offer ${offer.id}: ${error}`);
      return null;
    }
  });

  // Wait for all promises to resolve
  const results = await Promise.allSettled(fetchPromises);

  // Process results and build the map
  results.forEach((result) => {
    if (result.status === 'fulfilled' && result.value) {
      const { offerId, translations } = result.value;
      translationsMap.set(offerId, translations);
    }
  });

  return translationsMap;
};

/**
 * Prefetch and cache translations for all offers
 */
export const prefetchOfferTranslations = async (
  storefrontAccessToken: string,
  offers: Offer[],
  generalMetaobject: Record<string, string> | null,
  settings: Settings,
  cacheTranslations?: (
    offerId: number,
    translations: OfferTranslations,
  ) => void,
): Promise<void> => {
  if (offers.length === 0) {
    return;
  }

  try {
    const translationsMap = await batchFetchOfferTranslations(
      storefrontAccessToken,
      offers,
      generalMetaobject,
      settings,
    );

    // Cache the translations if cache function is provided
    if (cacheTranslations) {
      translationsMap.forEach((translations, offerId) => {
        cacheTranslations(offerId, translations);
      });
    }
  } catch (error) {
    logError(`Failed to prefetch offer translations: ${error}`);
  }
};
